import type { Moment } from 'moment'
import { useCallback, useContext, useState } from 'react'

import AdditionalDetails from './AdditionalDetails'
import CreateTripStart from './CreateTripStart'

import ModalBase from 'LuxKit/Modal/ModalBase'
import { ModalElementContext } from 'contexts/ModalProvider'
import noop from 'lib/function/noop'
import type { FullTrip } from 'tripPlanner/types/common'

interface Props {
  startDate?: Moment
  endDate?: Moment
  onCreateTrip?: (trip: FullTrip) => void
}

/**
 * This is one of two components that are almost the same, except this one is for use with the main CP modal context.
 * For use with the Trip Planner modal context, use CreateTripTP.tsx
 */
function CreateTripMC({
  startDate,
  endDate,
  onCreateTrip: onFinish = noop,
}: Props) {
  const [trip, setTrip] = useState<FullTrip | undefined>(undefined)

  const modalElement = useContext(ModalElementContext)

  const closeModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const onCreateTrip = useCallback((trip: FullTrip) => {
    setTrip(trip)
  }, [])

  const onAdditionalDetailsClose = useCallback(
    (trip: FullTrip) => {
      closeModal()
      onFinish(trip)
    },
    [closeModal, onFinish],
  )

  if (!trip) {
    return (
      <ModalBase size="S">
        <CreateTripStart
          startDate={startDate}
          endDate={endDate}
          onCancel={closeModal}
          onCreateTrip={onCreateTrip}
        />
      </ModalBase>
    )
  } else {
    return (
      <ModalBase size="L" height="auto">
        <AdditionalDetails
          trip={trip}
          onClose={onAdditionalDetailsClose}
          isInBookmarkingFlow={false}
        />
      </ModalBase>
    )
  }
}

export default CreateTripMC
